import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";

const LANGUAGE_LABELS: Record<string, string> = {
  AUTO: "Auto",
  ENGLISH: "English",
  GERMAN: "Deutsch",
  FRENCH: "Français",
  SPANISH: "Español",
  ITALIAN: "Italiano",
};

const motherTongue = "GERMAN";
const learningLanguages = ["FRENCH", "SPANISH"];

function detectLanguageStub(text: string): string {
  const lower = text.toLowerCase();
  if (/[äöüß]/.test(lower)) return "GERMAN";
  if (/[éèàçùâêîôûëïüÿ]/.test(lower)) return "FRENCH";
  if (/[áéíóúñü]/.test(lower)) return "SPANISH";
  if (/[àèéìíîòóù]/.test(lower)) return "ITALIAN";
  return "AUTO";
}

function QuickTranslatePage() {
  const [text, setText] = useState("");
  const [translated, setTranslated] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const detected = useMemo(() => detectLanguageStub(text), [text]);
  const hasText = Boolean(text.trim());

  const primaryTarget =
    detected === motherTongue
      ? learningLanguages[0]
      : motherTongue;

  const secondaryTargets =
    detected === motherTongue
      ? learningLanguages.slice(1)
      : learningLanguages.filter((l) => l !== detected && l !== primaryTarget);

  const doTranslate = (target: string) => {
    if (!text.trim()) return;
    setIsTranslating(true);
    // Spike: simulate translation locally. Wire to /api/translate later.
    setTimeout(() => {
      setTranslated(`(Stub) ${text} → ${LANGUAGE_LABELS[target] || target}`);
      setIsTranslating(false);
    }, 250);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Experiment
          </p>
          <h1 className="text-3xl font-bold text-slate-900">Quick Translate</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            One box, smart buttons. We detect the input language and offer the best target (mother tongue or your learning language).
          </p>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="flex flex-col gap-3">
            <label className="text-sm font-semibold text-slate-700">Text</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              placeholder="Type or paste a sentence…"
            />
            <p className="text-xs text-slate-500">
              Detected: {LANGUAGE_LABELS[detected] || "Auto"}
            </p>
          </div>

          {hasText ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => doTranslate(primaryTarget)}
                disabled={isTranslating}
                className="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-50"
              >
                {isTranslating ? "Translating…" : `To ${LANGUAGE_LABELS[primaryTarget] || primaryTarget}`}
              </button>
              {secondaryTargets.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => doTranslate(lang)}
                  disabled={isTranslating}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-blue-400 disabled:opacity-50"
                >
                  To {LANGUAGE_LABELS[lang] || lang}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500">Type or paste text to pick a target language.</p>
          )}

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 min-h-[80px]">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Translation
            </p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">
              {translated || "—"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/quick_translate")({
  component: QuickTranslatePage,
});
